import agentTools from "./schemas/tools/agent-tools.json";
import translations from "./i18n/translations.json";
import defaultAvatars from "./defaultAvatars.json";
import { supportedLocales } from "./languages";

export const defaultTranslations = translations["en-US"];

// Based on https://github.com/fictive-reality/schema/blob/v3/schemas/settings.json
// TODO make typesafe using generated code from schema repo
// TODO in the future may makes sense to store this setting on database root and read from there
// to allow different defaults in different environments, but it also adds one DB roundtrip
export const settings = {
  availableLocales: {
    "en-US": true,
    "en-GB": true,
    "sv-SE": true,
  },
  availableVoiceProviders: {
    azure: true,
    elevenlabs: false,
    google: false,
    openai: false,
  },
  availableTranscriberProviders: {
    azure: true,
    google: true,
  },
  availableEnvironments: {
    office: true,
    apartment: true,
    gameshow: true,
    shop: true,
    cocaColaShop: true,
    classroom: true,
    coffeeShop: true,
    doctor: true,
    gym: true,
    street: true,
  },
  preferredUILocale: "en-US",
  availableAvatars: defaultAvatars,
  // NOTE false means shown only to managers
  availableRatings: {
    attitude: true,
    engagement: false,
    fillers: true,
    gaze: false,
    gestures: false,
    headMovements: false,
    hesitation: true,
    pitch: true,
    sessionSummary: true,
    volume: true,
    wordRate: true,
  },
  availableAgentProviders: {
    openai: true,
    azureopenai: true,
    static: false,
    none: false,
  },
  backendChannel: "default",
  agentProvider: "openai",
  assessmentMode: false,
  webAvatarFeature: true,
  scenarioWizardFeature: true,
  inactivityLogoutMinutes: 0,
};

// NOTE true or false is not used here
export const supportedRatings = {
  alreadySaid: false,
  attitude: true,
  boredom: false,
  botLineIssues: false,
  botReadability: false,
  confusion: false,
  engagement: false,
  fillers: true,
  frustration: false,
  gaze: false,
  gestures: false,
  headMovements: true,
  hesitation: true,
  outOfCharacter: false,
  pitch: true,
  playerReadability: false,
  sessionSummary: true,
  volume: true,
  wordRate: true,
  wrongKnowledge: false,
};

export const isVideoEvaluation = evaluation => {
  return (
    evaluation === "gaze" ||
    evaluation === "engagement" ||
    evaluation === "frustration" ||
    evaluation === "confusion" ||
    evaluation === "boredom"
  );
};

export const formatEnvironmentOptions = availableEnvironments => {
  // TODO should superAdmins automatically get all availableEnvironments ?
  return Object.entries(availableEnvironments || settings.availableEnvironments)
    .filter(([key, val]) => supportedEnvironments[key])
    .filter(([key, val]) => val === true)
    .map(([key, val]) => ({ name: supportedEnvironments[key].name, image: supportedEnvironments[key].image }));
};

export const getEnvironment = environmentName => {
  if (!environmentName) return null;
  return Object.values(supportedEnvironments).find(env => env.name === environmentName);
};

import office from "../assets/environment_office.png";
import apartment from "../assets/environment_apartment.png";
import shop from "../assets/environment_shop.png";
import gameshow from "../assets/environment_game_show.png";
import cocaColaShop from "../assets/environment_cola.png";
import classroom from "../assets/environment_classroom.png";
import coffeeShop from "../assets/environment_coffeeshop.png";
import doctor from "../assets/environment_doctor.png";
import gym from "../assets/environment_schoolgym.png";
import street from "../assets/environment_street.png";

export const supportedEnvironments = {
  office: { name: "Office", image: office, backgroundUrl: "common/backgrounds/office360.hdr" },
  apartment: { name: "Apartment", image: apartment, backgroundUrl: "common/backgrounds/apartment360.hdr" },
  gameshow: { name: "Game show", image: gameshow, backgroundUrl: "common/backgrounds/show360.hdr" },
  shop: { name: "Shop", image: shop, backgroundUrl: "common/backgrounds/shop360.hdr" },
  cocaColaShop: { name: "CocaCola shop", image: cocaColaShop, backgroundUrl: "common/backgrounds/shop360.hdr" },
  classroom: { name: "Classroom", image: classroom, backgroundUrl: "common/backgrounds/classroom.jpg" },
  coffeeShop: { name: "Coffee shop", image: coffeeShop, backgroundUrl: "common/backgrounds/coffeeshop360.hdr" },
  doctor: {
    name: "Doctor's office",
    image: doctor,
    backgroundUrl: "common/backgrounds/doctor360.hdr",
  },
  gym: { name: "School Gym", image: gym, backgroundUrl: "common/backgrounds/gym360.hdr" },
  street: { name: "Street", image: street, backgroundUrl: "common/backgrounds/street360.hdr" },
};

/**
 * Formats which voices should be selectable, based on all downloaded (supported) voices, defined locale, gender and
 * which voice providers are configured to be available.
 * @param {Object} allVoices
 * @param {string[]} locale
 * @param {string} gender
 * @param {*} availableVoiceProviders
 * @param {boolean} showDeprecated
 * @returns
 */
export const formatVoiceOptions = (allVoices, locales, gender, availableVoiceProviders, showDeprecated) => {
  // TODO should superAdmins automatically get all availableVoiceProviders ?
  if (gender === "other") gender = null; // Count other as undefined, e.g. match all
  const rv = Object.entries(allVoices || {})
    .filter(([voiceName, voice]) => {
      return !availableVoiceProviders || availableVoiceProviders?.[voice?.provider] === true;
    })
    .filter(([voiceName, voice]) => {
      return showDeprecated || voice?.deprecated !== true;
    })
    .filter(([voiceName, voice]) => {
      return (
        (!gender || voice?.genders?.includes(gender)) && (!locales || locales.every(l => voice?.locales?.includes(l)))
      );
    })
    .map(([voiceName, voice]) => ({
      voiceName,
      description: voice.description,
      genders: voice.genders,
      recommended: voice.recommended,
    }))
    .sort((a, b) => {
      if (a.recommended && !b.recommended) return -1;
      if (!a.recommended && b.recommended) return 1;
      return a.voiceName.localeCompare(b.voiceName);
    });
  return rv;
};

// This is a hacky way to guess gender from a voice. If a voice has multiple genders, we just pick the first one.
export const voiceNameToGender = (allVoices, voiceName) => allVoices?.[voiceName]?.genders?.[0];

export const supportedVoiceProviders = {
  azure: { id: "azure", name: "Azure", label: "Azure", value: "azure", suffix: "-A" },
  elevenlabs: { id: "elevenlabs", name: "Eleven Labs", label: "Eleven Labs", value: "elevenlabs", suffix: "-E" },
  google: { id: "google", name: "Google", label: "Google", value: "google", suffix: "-G" },
  openai: { id: "openai", name: "OpenAI", label: "OpenAI", value: "openai", suffix: "-O" },
};

export const formatTranscriberProviderOptions = availableTranscriberProviders => {
  return Object.entries(availableTranscriberProviders || settings.availableTranscriberProviders)
    .filter(([key, value]) => supportedTranscriberProviders[key] && value === true)
    .map(([key]) => ({ id: key, name: supportedTranscriberProviders[key].name }));
};

export const supportedTranscriberProviders = {
  azure: { name: "Azure" },
  google: { name: "Google" },
};

export const formatAgentProviderOptions = availableAgentProviders => {
  // TODO should superAdmins automatically get all agent providers ?
  return Object.entries(availableAgentProviders || settings.availableAgentProviders)
    .filter(([key, val]) => supportedAgentProviders[key])
    .filter(([key, val]) => val === true)
    .map(([key, val]) => ({ id: key, name: supportedAgentProviders[key].name }));
};

export const supportedAgentProviders = {
  openai: { name: "OpenAI Global" },
  azureopenai: { name: "Azure OpenAI Sweden" },
  anthropic: { name: "Anthropic Global" },
  google: { name: "Google Europe" },
  scripted: { name: "Scripted (no LLM)" },
  none: { name: "None" },
};

export const genderOptions = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
];

// Every tool in schemas/tools/agent-tools.json.
export const agentToolDefinitions = agentTools;

// The tools convai forwards to the client, which VoiceChat has to implement.
export const clientToolNames = Object.keys(agentTools).filter(name => agentTools[name].executedBy === "client");

// The tools a scenario can choose from. Always-enabled tools are left out: they are on
// in every session, so a toggle for them would not do anything.
export const supportedTools = Object.fromEntries(
  Object.entries(agentTools)
    .filter(([, tool]) => !tool.alwaysEnabled)
    .map(([name, tool]) => [name, { name: tool.title, description: tool.help }])
);

// Makes it easier to lookup which keys can be used for which settings
// Note that some available<X> settings can contain random IDs, e.g. voices, avatars,
// that need to be filled by checking the database
export const supportedKeys = {
  agentTools: supportedTools,
  availableRatings: supportedRatings,
  availableLocales: supportedLocales,
  availableAgentProviders: supportedAgentProviders,
  availableVoiceProviders: supportedVoiceProviders,
  availableEnvironments: supportedEnvironments,
};

export const aiActorQuestions = {
  aiActorExtraversionVsIntroversion: "The AI Actor feels energized when interacting with a group of people",
  aiActorSensingVsIntuition: "The AI Actor prefers focusing on facts and details over abstract ideas",
  aiActorThinkingVsFeeling:
    "The AI Actor makes decisions based on logic and subjectivity rather than personal feelings",
  aiActorJudgingVsPerceiving: "The AI Actor likes having a structured schedule and prefers to plan ahead",
  aiActorAssertiveVsTurbulent:
    "The AI Actor is confident in its abilities and rarely feels anxious about its decisions",
};

export const SUGGESTION_REACTIONS = {
  LIKED: "liked", // the suggestion was liked, e.g. thumbs up
  DISLIKED: "disliked", // the suggestion was disliked, e.g. thumbs down
  IGNORED: "ignored", // the suggestion was ignored, e.g. never shown
  DISCARDED: "discarded", // the suggestion was discarded, e.g. not saved presumably due to user choice
  EDITED: "edited", // the suggestion was saved, but with changes
  ACCEPTED: "accepted", // the suggestion was saved, as is
};

export const SUPPORTED_DOCUMENT_TYPES = [
  ".doc",
  ".docx",
  ".pdf",
  ".rtf",
  ".ppt",
  ".pptx",
  ".odt",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
  "application/rtf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
];

export const SUPPORTED_SPREADSHEET_TYPES = [
  ".xls",
  ".xlsx",
  ".csv",
  ".ods",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/vnd.oasis.opendocument.spreadsheet",
];

export const SUPPORTED_IMAGE_TYPES = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".tiff",
  ".tif",
  ".svg",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",
  "image/tiff",
  "image/svg+xml",
];

export const SUPPORTED_TEXT_TYPES = [".txt", ".html", ".md", ".markdown", "text/html", "text/markdown", "text/plain"];

export const ALL_SUPPORTED_TYPES = [
  ...SUPPORTED_DOCUMENT_TYPES,
  ...SUPPORTED_SPREADSHEET_TYPES,
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_TEXT_TYPES,
].join(",");
